import { numFont } from '../../../utils/fonts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import BuildOutlined from '@mui/icons-material/BuildOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import SyncOutlined from '@mui/icons-material/SyncOutlined';
import { formatPrice, formatPriceWithCurrency, calcPriceInclVat } from '../../../utils/vat';
import PartStatusRow from './PartStatusRow';

export default function OemPreviewPane({ packageData, hourlyRate, priceIncludesVat, onSwapPart, selectedVariants, onRefresh, lastUpdated }) {
  const { t } = useTranslation();

  if (!packageData) return null;

  const { resolved_parts = [], tasks = [], service_time, name } = packageData;

  const laborTotalExclVat = (service_time || 0) * (hourlyRate || 0);
  const partsTotalExclVat = resolved_parts.reduce((sum, p) => {
    const price = parseFloat(
      p.matched_part_price ||
      p.auto_picked?.sales_price ||
      p.auto_picked?.purchase_price ||
      p.catalogue_candidate?.purchase_price ||
      0
    );
    return sum + price * (p.quantity || 1);
  }, 0);
  const totalExclVat = laborTotalExclVat + partsTotalExclVat;
  const grandTotal = priceIncludesVat ? calcPriceInclVat(totalExclVat) : totalExclVat;

  const okCount = resolved_parts.filter(p => p.status === 'ok').length;
  const ambiguousCount = resolved_parts.filter(p => p.status === 'ambiguous').length;
  const missingCount = resolved_parts.filter(p => p.status === 'missing').length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {t('oem.preview')}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            fontSize: 11,
            color: '#2f8c54',
            bgcolor: '#e6ffed',
            px: 1.25,
            py: 0.5,
            borderRadius: 20,
            fontWeight: 500,
          }}
        >
          <SyncOutlined sx={{ fontSize: 12 }} />
          {lastUpdated
            ? t('oem.updatedAgo', { minutes: lastUpdated })
            : t('oem.justUpdated')}
          {onRefresh && (
            <IconButton size="small" onClick={onRefresh} sx={{ p: 0, ml: 0.25, color: '#2f8c54' }}>
              <SyncOutlined sx={{ fontSize: 12 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Jobs card */}
      <Box sx={{ bgcolor: '#fff', border: 1, borderColor: 'divider', borderRadius: '12px', p: '14px 16px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <BuildOutlined sx={{ fontSize: 16, color: 'secondary.main' }} />
            {t('oem.jobs')}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{tasks.length}</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Main labor line */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.875, borderBottom: '1px dashed', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 13 }}>{name || t('oem.periodicMaintenance')}</Typography>
            <Typography sx={{ ...numFont, color: 'text.secondary', fontSize: 13, flexShrink: 0, ml: 1.5 }}>
              {service_time?.toFixed(1)}h · {formatPriceWithCurrency(priceIncludesVat ? calcPriceInclVat(laborTotalExclVat) : laborTotalExclVat)}
            </Typography>
          </Box>

          {/* Task checklist */}
          {tasks.map((task, i) => (
            <Box
              key={task.task_id || i}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.875,
                borderBottom: i < tasks.length - 1 ? '1px dashed' : 'none',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontSize: 12, color: 'text.secondary', minWidth: 0, overflowWrap: 'anywhere' }}>
                {task.action_fi} — {task.description}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.disabled', fontStyle: 'italic', flexShrink: 0, ml: 1 }}>
                {task.position_fi}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Parts card */}
      <Box sx={{ bgcolor: '#fff', border: 1, borderColor: 'divider', borderRadius: '12px', p: '14px 16px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Inventory2Outlined sx={{ fontSize: 16, color: 'secondary.main' }} />
            {t('oem.partsLabel')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, fontSize: 11 }}>
            {okCount > 0 && <Typography sx={{ color: '#2f8c54', fontSize: 'inherit' }}>{okCount} ✓</Typography>}
            {ambiguousCount > 0 && <Typography sx={{ color: '#b45309', fontSize: 'inherit' }}>{ambiguousCount} ?</Typography>}
            {missingCount > 0 && <Typography sx={{ color: '#dc2626', fontSize: 'inherit' }}>{missingCount} ✗</Typography>}
          </Box>
        </Box>

        {resolved_parts.length > 0 && (
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 1 }}>
            {t('oem.partsFoundSummary', { found: okCount, total: resolved_parts.length })}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {resolved_parts.map((part, i) => (
            <PartStatusRow
              key={`${part.ezyvin_part_name}-${i}`}
              part={part}
              onSwap={onSwapPart}
              selectedVariant={selectedVariants?.[part.ezyvin_part_name]}
            />
          ))}
        </Box>
      </Box>

      {/* Total bar */}
      <Box
        sx={{
          bgcolor: '#1a365d',
          color: '#fff',
          borderRadius: '12px',
          p: '14px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
            {t('oem.total')}
          </Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.7, mt: 0.25 }}>
            {service_time?.toFixed(1)}h · {resolved_parts.length} {t('oem.parts')} · {priceIncludesVat ? t('oem.inclVat') : t('oem.exclVat')}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 24, fontWeight: 700, ...numFont, flexShrink: 0 }}>
          {formatPriceWithCurrency(grandTotal)}
        </Typography>
      </Box>
    </Box>
  );
}
